import {Component, forwardRef, EventEmitter, ElementRef, ViewChild, ViewEncapsulation, HostBinding} from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor, NgModel, FormGroup, AbstractControl} from '@angular/forms';
import {Input, Output} from '@angular/core';
import {Observable} from 'rxjs';
import {FormValidationService} from './form-validation.service';
import {Utils} from './utils.service';
// TODO: ld-tooltip will be replaced by ula-tooltip; this is a breaking change
import { TooltipContentComponent } from '../tooltip';

const noop = () => {};

export const ULA_EMAIL_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => UlaEmailInputComponent),
  multi: true
};


let nextUniqueId = 0;

// More reading: https://github.com/angular/material2/blob/master/src/lib/input/input.ts
// More reading: http://almerosteyn.com/2016/04/linkup-custom-control-to-ngcontrol-ngmodel
@Component({
  selector: 'ula-email-input',
  templateUrl: './ula-email-input.template.html',
  styleUrls: ['./ula-input.style.css'],
  providers: [ULA_EMAIL_INPUT_CONTROL_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None
})
  export class UlaEmailInputComponent implements ControlValueAccessor {
  id: string = `ld-email-input-${nextUniqueId++}`;

  // omitting the '_class' variable name doesn't seem to correctly apply the CSS class,
  // so don't omit it
  @HostBinding('class') _class = 'd-block';

  public counter: number = nextUniqueId++;
  public _valid: boolean = true;
  public _readonly: boolean = false;

  @ViewChild('input') input: ElementRef;
  @ViewChild('inputModel') inputModel: NgModel;

  public _focused: boolean = false;
  public _value: any = '';
  public _elementType: 'input' | 'textarea';

  private _onTouchedCallback: () => void = noop;
  private _onChangeCallback: (_: any) => void = noop;

  /** Readonly properties. */
  get empty() { return (this._value == null || this._value === '') && this.type !== 'date'; }

  get inputName(): string { return `${this.name}${this.counter}`; }

  private formControl: AbstractControl;
  @Input() validationForm: FormGroup;
  @Input() displayName: string;
  @Input() formControlName: string;
  @Input() shouldCheckIsDirty: boolean = false;
  @Input() hideError: boolean = false;

  /**
   * Misc
   */
  @Input() title: string = '';
  @Input() labelPrefix: string;
  @Input() label: string;
  @Input() defaultTitle: string = '';
  // TODO: ld-tooltip will be replaced by ula-tooltip; this is a breaking change
  @Input() popover: string | TooltipContentComponent;
  @Input() titleIconBefore: string = null;
  @Input() titleIconAfter: string = null;

  /**
   * Bindings.
   */
  @Input() max: string | number = null;
  @Input() maxlength: number = null;
  @Input() min: string | number = null;
  @Input() minlength: number = null;
  @Input() placeholder: string = null;
  @Input() step: number = null;
  @Input() type: string = 'text';
  @Input() name: string = null;
  @Input() qa: string = null;
  @Input() mask: string = null;

  private validEmailTest: RegExp = new RegExp(this.formValidation.regex.email);
  public isValidEmail: boolean = false;
  private _autofocus: boolean = false;
  private _disabled: boolean = false;
  private _required: boolean = false;
  private _spellcheck: boolean = false;

  @Input()
  get autofocus(): boolean { return this._autofocus; }
  set autofocus(value) { this._autofocus = this.coerceBooleanProperty(value); }

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value) { this._disabled = this.coerceBooleanProperty(value); }

  @Input()
  get required(): boolean { return this._required; }
  set required(value) { this._required = this.coerceBooleanProperty(value); }

  @Input()
  get spellcheck(): boolean { return this._spellcheck; }
  set spellcheck(value) { this._spellcheck = this.coerceBooleanProperty(value); }

  @Input()
  get readonly(): boolean { return this._readonly; }
  set readonly(value) { this._readonly = this.coerceBooleanProperty(value); }

  private _blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  private _focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  @Output('blur')
  get onBlur(): Observable<FocusEvent> { return this._blurEmitter.asObservable(); }

  @Output('focus')
  get onFocus(): Observable<FocusEvent> { return this._focusEmitter.asObservable(); }

  get value(): any { return this._value; };

  @Input() set value(v: any) {
    v = this._convertValueForInputType(v);

    if (this.type === 'number' && this.max && (v > this.max)) {
      v = this._value;
      this.input.nativeElement.value = this._value;
    }

    // input type="number" doesn't support the maxlength attribute, so fake it here
    if (this.type === 'number' && this.maxlength && (v.toString().length > this.maxlength)) {
      v = this._value;
      this.input.nativeElement.value = this._value;
    }

    if (v !== this._value) {
      this.isValidEmail = !!v && this.validEmailTest.test(v);
      this._value = v;
      this._onChangeCallback(v);
    }
  }


  constructor(elementRef: ElementRef,
              public formValidation: FormValidationService,
              private utils: Utils) {
    this._elementType = elementRef.nativeElement.nodeName.toLowerCase() === 'ld-textarea' ? 'textarea' : 'input';
  }

  hasErrors() {
    if (!this.validationForm) {
      return false;
    }

    if (!this.formControl) {
      this.formControl = this.validationForm.controls[this.formControlName];
    }

    // Have to put a check on this._focused
    return (
      this.formControl.errors && (this.isValidEmail || !this._focused) &&
      (this.formControl.touched || this.formControl.dirty || !this.shouldCheckIsDirty)
    );
  }

  _handleFocus(event: FocusEvent) {
    this._focused = true;
    this._focusEmitter.emit(event);
  }

  _handleBlur(event: FocusEvent) {
    this._focused = false;
    this._onTouchedCallback();
    this._blurEmitter.emit(event);
  }

  _handleChange(event: Event) {
    this.value = (<HTMLInputElement>event.target).value.split(/\s+/).join('');
    this._onTouchedCallback();
  }

  _handleKeyPress(event: KeyboardEvent) {
    // disallow spaces
    if (event.which === 32) {
      event.preventDefault();
    }
  }

  public focus(): void {
    this.input.nativeElement.focus();
  }

  writeValue(value: any) {
    this._value = value;
  }

  registerOnChange(fn: any) {
    this._onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouchedCallback = fn;
  }

  coerceBooleanProperty(value: any): boolean {
    return value != null && `${value}` !== 'false';
  }

  /**
   * Convert the value passed in to a value that is expected from the type of the md-input.
   * This is normally performed by the *_VALUE_ACCESSOR in forms, but since the type is bound
   * on our internal input it won't work locally.
   * @private
   */
  private _convertValueForInputType(v: any): any {
    let returnValue = v;
    switch (this.type) {
      case 'number':
        returnValue = this.utils.convertNumberValue(v);
        break;
      default:
        break;
    }

    return returnValue;
  }
}
